import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Save, X, Award, MessageSquare, FileText } from 'lucide-react';
import { toast } from 'react-toastify';

const ImpactManagement = () => {
    const ARCA_GREEN = '#6B8E23';
    const API_BASE = process.env.REACT_APP_API_URL;

    if (!API_BASE) {
        console.error('❌ CRITICAL: REACT_APP_API_URL not configured in ImpactManagement!');
    }

    const [activeTab, setActiveTab] = useState('stories');
    const [loading, setLoading] = useState(false);

    // Success Stories State
    const [stories, setStories] = useState([]);
    const [showStoryModal, setShowStoryModal] = useState(false);
    const [editingStory, setEditingStory] = useState(null);
    const [storyForm, setStoryForm] = useState({
        title: '',
        location: '',
        date: '',
        description: '',
        impact: '',
        category: '',
        icon: 'BookOpen',
        image_url: '',
        display_order: 0
    });

    // Testimonials State
    const [testimonials, setTestimonials] = useState([]);
    const [showTestimonialModal, setShowTestimonialModal] = useState(false);
    const [editingTestimonial, setEditingTestimonial] = useState(null);
    const [testimonialForm, setTestimonialForm] = useState({
        content: '',
        author: '',
        role: '',
        country: '',
        rating: 5,
        image_url: '',
        display_order: 0
    });

    // Annual Reports State
    const [reports, setReports] = useState([]);
    const [showReportModal, setShowReportModal] = useState(false);
    const [editingReport, setEditingReport] = useState(null);
    const [reportForm, setReportForm] = useState({
        year: new Date().getFullYear(),
        title: '',
        description: '',
        cover_image_url: '',
        pdf_url: '',
        file_size: '',
        is_published: false
    });

    const iconOptions = [
        { value: 'BookOpen', label: 'Book Open' },
        { value: 'Users', label: 'Users' },
        { value: 'Globe', label: 'Globe' },
        { value: 'TrendingUp', label: 'Trending Up' },
        { value: 'Award', label: 'Award' },
        { value: 'Target', label: 'Target' }
    ];

    useEffect(() => {
        if (activeTab === 'stories') fetchStories();
        else if (activeTab === 'testimonials') fetchTestimonials();
        else if (activeTab === 'reports') fetchReports();
    }, [activeTab]);

    // ============================================
    // SUCCESS STORIES FUNCTIONS
    // ============================================

    const fetchStories = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('adminToken');

            if (!token) {
                console.error('No admin token found');
                toast.error('Please login as admin first');
                setLoading(false);
                return;
            }

            const response = await fetch(`${API_BASE}/admin/impact/success-stories`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            console.log('Success stories response:', data);

            if (data.success) {
                setStories(data.data || []);
                console.log('Loaded stories:', data.data?.length || 0);
            } else {
                toast.error(data.message || 'Failed to load success stories');
            }
        } catch (error) {
            console.error('Error fetching success stories:', error);
            toast.error('Failed to load success stories: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSaveStory = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('adminToken');
            const url = editingStory
                ? `${API_BASE}/admin/impact/success-stories/${editingStory.id}`
                : `${API_BASE}/admin/impact/success-stories`;

            const response = await fetch(url, {
                method: editingStory ? 'PUT' : 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(storyForm)
            });

            const data = await response.json();

            if (data.success) {
                setShowStoryModal(false);
                resetStoryForm();
                fetchStories();
                toast.success(`Success story ${editingStory ? 'updated' : 'created'} successfully`);
            } else {
                toast.error(data.message || 'Operation failed');
            }
        } catch (error) {
            console.error('Error saving success story:', error);
            toast.error('Failed to save success story');
        }
    };

    const handleEditStory = (story) => {
        setEditingStory(story);
        setStoryForm({
            title: story.title || '',
            location: story.location || '',
            date: story.date || '',
            description: story.description || '',
            impact: story.impact || '',
            category: story.category || '',
            icon: story.icon || 'BookOpen',
            image_url: story.image_url || '',
            display_order: story.display_order || 0
        });
        setShowStoryModal(true);
    };

    const handleDeleteStory = async (id) => {
        if (!window.confirm('Are you sure you want to delete this success story?')) return;

        try {
            const token = localStorage.getItem('adminToken');
            const response = await fetch(`${API_BASE}/admin/impact/success-stories/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            const data = await response.json();

            if (data.success) {
                fetchStories();
                toast.success('Success story deleted successfully');
            } else {
                toast.error(data.message || 'Failed to delete success story');
            }
        } catch (error) {
            console.error('Error deleting success story:', error);
            toast.error('Failed to delete success story');
        }
    };

    const resetStoryForm = () => {
        setEditingStory(null);
        setStoryForm({
            title: '',
            location: '',
            date: '',
            description: '',
            impact: '',
            category: '',
            icon: 'BookOpen',
            image_url: '',
            display_order: 0
        });
    };

    // ============================================
    // TESTIMONIALS FUNCTIONS
    // ============================================

    const fetchTestimonials = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('adminToken');

            if (!token) {
                console.error('No admin token found');
                toast.error('Please login as admin first');
                setLoading(false);
                return;
            }

            const response = await fetch(`${API_BASE}/admin/impact/testimonials`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            console.log('Testimonials response:', data);

            if (data.success) {
                setTestimonials(data.data || []);
                console.log('Loaded testimonials:', data.data?.length || 0);
            } else {
                toast.error(data.message || 'Failed to load testimonials');
            }
        } catch (error) {
            console.error('Error fetching testimonials:', error);
            toast.error('Failed to load testimonials: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSaveTestimonial = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('adminToken');
            const url = editingTestimonial
                ? `${API_BASE}/admin/impact/testimonials/${editingTestimonial.id}`
                : `${API_BASE}/admin/impact/testimonials`;

            const response = await fetch(url, {
                method: editingTestimonial ? 'PUT' : 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(testimonialForm)
            });

            const data = await response.json();

            if (data.success) {
                setShowTestimonialModal(false);
                resetTestimonialForm();
                fetchTestimonials();
                toast.success(`Testimonial ${editingTestimonial ? 'updated' : 'created'} successfully`);
            } else {
                toast.error(data.message || 'Operation failed');
            }
        } catch (error) {
            console.error('Error saving testimonial:', error);
            toast.error('Failed to save testimonial');
        }
    };

    const handleEditTestimonial = (testimonial) => {
        setEditingTestimonial(testimonial);
        setTestimonialForm({
            content: testimonial.content || '',
            author: testimonial.author || '',
            role: testimonial.role || '',
            country: testimonial.country || '',
            rating: testimonial.rating || 5,
            image_url: testimonial.image_url || '',
            display_order: testimonial.display_order || 0
        });
        setShowTestimonialModal(true);
    };

    const handleDeleteTestimonial = async (id) => {
        if (!window.confirm('Are you sure you want to delete this testimonial?')) return;

        try {
            const token = localStorage.getItem('adminToken');
            const response = await fetch(`${API_BASE}/admin/impact/testimonials/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            const data = await response.json();

            if (data.success) {
                fetchTestimonials();
                toast.success('Testimonial deleted successfully');
            } else {
                toast.error(data.message || 'Failed to delete testimonial');
            }
        } catch (error) {
            console.error('Error deleting testimonial:', error);
            toast.error('Failed to delete testimonial');
        }
    };

    const resetTestimonialForm = () => {
        setEditingTestimonial(null);
        setTestimonialForm({
            content: '',
            author: '',
            role: '',
            country: '',
            rating: 5,
            image_url: '',
            display_order: 0
        });
    };

    // ============================================
    // ANNUAL REPORTS FUNCTIONS
    // ============================================

    const fetchReports = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('adminToken');

            if (!token) {
                console.error('No admin token found');
                toast.error('Please login as admin first');
                setLoading(false);
                return;
            }

            const response = await fetch(`${API_BASE}/admin/impact/annual-reports`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            console.log('Annual reports response:', data);

            if (data.success) {
                setReports(data.data || []);
                console.log('Loaded reports:', data.data?.length || 0);
            } else {
                toast.error(data.message || 'Failed to load annual reports');
            }
        } catch (error) {
            console.error('Error fetching annual reports:', error);
            toast.error('Failed to load annual reports: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSaveReport = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('adminToken');
            const url = editingReport
                ? `${API_BASE}/admin/impact/annual-reports/${editingReport.id}`
                : `${API_BASE}/admin/impact/annual-reports`;

            const response = await fetch(url, {
                method: editingReport ? 'PUT' : 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(reportForm)
            });

            const data = await response.json();

            if (data.success) {
                setShowReportModal(false);
                resetReportForm();
                fetchReports();
                toast.success(`Annual report ${editingReport ? 'updated' : 'created'} successfully`);
            } else {
                toast.error(data.message || 'Operation failed');
            }
        } catch (error) {
            console.error('Error saving annual report:', error);
            toast.error('Failed to save annual report');
        }
    };

    const handleEditReport = (report) => {
        setEditingReport(report);
        setReportForm({
            year: report.year || new Date().getFullYear(),
            title: report.title || '',
            description: report.description || '',
            cover_image_url: report.cover_image_url || '',
            pdf_url: report.pdf_url || '',
            file_size: report.file_size || '',
            is_published: report.is_published || false
        });
        setShowReportModal(true);
    };

    const handleDeleteReport = async (id) => {
        if (!window.confirm('Are you sure you want to delete this annual report?')) return;

        try {
            const token = localStorage.getItem('adminToken');
            const response = await fetch(`${API_BASE}/admin/impact/annual-reports/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            const data = await response.json();

            if (data.success) {
                fetchReports();
                toast.success('Annual report deleted successfully');
            } else {
                toast.error(data.message || 'Failed to delete annual report');
            }
        } catch (error) {
            console.error('Error deleting annual report:', error);
            toast.error('Failed to delete annual report');
        }
    };

    const resetReportForm = () => {
        setEditingReport(null);
        setReportForm({
            year: new Date().getFullYear(),
            title: '',
            description: '',
            cover_image_url: '',
            pdf_url: '',
            file_size: '',
            is_published: false
        });
    };

    return (
        <div className="container-fluid py-4">
            {/* Login Status Check */}
            {!localStorage.getItem('adminToken') && (
                <div className="alert alert-warning mb-4">
                    <strong>⚠️ Not Logged In:</strong> You must be logged in as admin to view and manage data. Please <a href="/login" className="alert-link">login here</a>.
                </div>
            )}

            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="h3 mb-1" style={{ color: ARCA_GREEN }}>
                        <Award size={28} className="me-2" style={{ verticalAlign: 'middle' }} />
                        Impact Management
                    </h2>
                    <p className="text-muted mb-0">Manage success stories, testimonials, and annual reports</p>
                </div>
            </div>

            {/* Tabs */}
            <div className="mb-4">
                <div className="btn-group" role="group" style={{ width: '100%', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    <button
                        type="button"
                        className={`btn ${activeTab === 'stories' ? 'btn-success' : 'btn-outline-secondary'}`}
                        onClick={() => setActiveTab('stories')}
                        style={{
                            flex: '1',
                            minWidth: '200px',
                            padding: '12px 20px',
                            fontSize: '16px',
                            fontWeight: '600',
                            backgroundColor: activeTab === 'stories' ? ARCA_GREEN : '#fff',
                            borderColor: activeTab === 'stories' ? ARCA_GREEN : '#6c757d',
                            color: activeTab === 'stories' ? '#fff' : '#333'
                        }}
                    >
                        <Award size={20} className="me-2" style={{ verticalAlign: 'middle' }} />
                        Success Stories
                    </button>
                    <button
                        type="button"
                        className={`btn ${activeTab === 'testimonials' ? 'btn-success' : 'btn-outline-secondary'}`}
                        onClick={() => setActiveTab('testimonials')}
                        style={{
                            flex: '1',
                            minWidth: '200px',
                            padding: '12px 20px',
                            fontSize: '16px',
                            fontWeight: '600',
                            backgroundColor: activeTab === 'testimonials' ? ARCA_GREEN : '#fff',
                            borderColor: activeTab === 'testimonials' ? ARCA_GREEN : '#6c757d',
                            color: activeTab === 'testimonials' ? '#fff' : '#333'
                        }}
                    >
                        <MessageSquare size={20} className="me-2" style={{ verticalAlign: 'middle' }} />
                        Testimonials
                    </button>
                    <button
                        type="button"
                        className={`btn ${activeTab === 'reports' ? 'btn-success' : 'btn-outline-secondary'}`}
                        onClick={() => setActiveTab('reports')}
                        style={{
                            flex: '1',
                            minWidth: '200px',
                            padding: '12px 20px',
                            fontSize: '16px',
                            fontWeight: '600',
                            backgroundColor: activeTab === 'reports' ? ARCA_GREEN : '#fff',
                            borderColor: activeTab === 'reports' ? ARCA_GREEN : '#6c757d',
                            color: activeTab === 'reports' ? '#fff' : '#333'
                        }}
                    >
                        <FileText size={20} className="me-2" style={{ verticalAlign: 'middle' }} />
                        Annual Reports
                    </button>
                </div>
            </div>

            {/* Success Stories Tab */}
            {activeTab === 'stories' && (
                <div>
                    <div className="mb-3">
                        <button
                            className="btn text-white"
                            style={{ backgroundColor: ARCA_GREEN }}
                            onClick={() => {
                                resetStoryForm();
                                setShowStoryModal(true);
                            }}
                        >
                            <Plus size={18} className="me-2" />
                            Add Success Story
                        </button>
                    </div>

                    {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border" style={{ color: ARCA_GREEN }} role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : (
                        <div className="card shadow-sm">
                            <div className="card-body">
                                <div className="table-responsive">
                                    <table className="table table-hover">
                                        <thead style={{ backgroundColor: '#f8f9fa' }}>
                                            <tr>
                                                <th>Order</th>
                                                <th>Title</th>
                                                <th>Location</th>
                                                <th>Category</th>
                                                <th>Date</th>
                                                <th>Status</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {stories.length === 0 ? (
                                                <tr>
                                                    <td colSpan="7" className="text-center py-5">
                                                        <div className="text-muted">
                                                            <Award size={48} className="mb-3" style={{ opacity: 0.3 }} />
                                                            <h5>No Success Stories Found</h5>
                                                            <p>Click "Add Success Story" to create your first entry,<br />or make sure you're logged in as admin to view existing data.</p>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ) : (
                                                stories.map((story) => (
                                                    <tr key={story.id}>
                                                        <td>{story.display_order}</td>
                                                        <td>
                                                            <strong>{story.title}</strong>
                                                            <br />
                                                            <small className="text-muted">{story.description?.substring(0, 50)}...</small>
                                                        </td>
                                                        <td><small>{story.location}</small></td>
                                                        <td><span className="badge bg-secondary">{story.category}</span></td>
                                                        <td><small>{story.date}</small></td>
                                                        <td>
                                                            <span className={`badge ${story.is_active ? 'bg-success' : 'bg-secondary'}`}>
                                                                {story.is_active ? 'Active' : 'Inactive'}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <button
                                                                className="btn btn-sm btn-outline-primary me-2"
                                                                onClick={() => handleEditStory(story)}
                                                                title="Edit"
                                                            >
                                                                <Edit2 size={16} />
                                                            </button>
                                                            <button
                                                                className="btn btn-sm btn-outline-danger"
                                                                onClick={() => handleDeleteStory(story.id)}
                                                                title="Delete"
                                                            >
                                                                <Trash2 size={16} />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Testimonials Tab */}
            {activeTab === 'testimonials' && (
                <div>
                    <div className="mb-3">
                        <button
                            className="btn text-white"
                            style={{ backgroundColor: ARCA_GREEN }}
                            onClick={() => {
                                resetTestimonialForm();
                                setShowTestimonialModal(true);
                            }}
                        >
                            <Plus size={18} className="me-2" />
                            Add Testimonial
                        </button>
                    </div>

                    {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border" style={{ color: ARCA_GREEN }} role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : (
                        <div className="card shadow-sm">
                            <div className="card-body">
                                <div className="table-responsive">
                                    <table className="table table-hover">
                                        <thead style={{ backgroundColor: '#f8f9fa' }}>
                                            <tr>
                                                <th>Order</th>
                                                <th>Author</th>
                                                <th>Content</th>
                                                <th>Country</th>
                                                <th>Rating</th>
                                                <th>Status</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {testimonials.length === 0 ? (
                                                <tr>
                                                    <td colSpan="7" className="text-center text-muted py-4">
                                                        No testimonials found
                                                    </td>
                                                </tr>
                                            ) : (
                                                testimonials.map((testimonial) => (
                                                    <tr key={testimonial.id}>
                                                        <td>{testimonial.display_order}</td>
                                                        <td>
                                                            <strong>{testimonial.author}</strong>
                                                            <br />
                                                            <small className="text-muted">{testimonial.role}</small>
                                                        </td>
                                                        <td><small>{testimonial.content?.substring(0, 60)}...</small></td>
                                                        <td><small>{testimonial.country}</small></td>
                                                        <td>
                                                            {'⭐'.repeat(testimonial.rating)}
                                                        </td>
                                                        <td>
                                                            <span className={`badge ${testimonial.is_active ? 'bg-success' : 'bg-secondary'}`}>
                                                                {testimonial.is_active ? 'Active' : 'Inactive'}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <button
                                                                className="btn btn-sm btn-outline-primary me-2"
                                                                onClick={() => handleEditTestimonial(testimonial)}
                                                                title="Edit"
                                                            >
                                                                <Edit2 size={16} />
                                                            </button>
                                                            <button
                                                                className="btn btn-sm btn-outline-danger"
                                                                onClick={() => handleDeleteTestimonial(testimonial.id)}
                                                                title="Delete"
                                                            >
                                                                <Trash2 size={16} />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Annual Reports Tab */}
            {activeTab === 'reports' && (
                <div>
                    <div className="mb-3">
                        <button
                            className="btn text-white"
                            style={{ backgroundColor: ARCA_GREEN }}
                            onClick={() => {
                                resetReportForm();
                                setShowReportModal(true);
                            }}
                        >
                            <Plus size={18} className="me-2" />
                            Add Annual Report
                        </button>
                    </div>

                    {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border" style={{ color: ARCA_GREEN }} role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : (
                        <div className="card shadow-sm">
                            <div className="card-body">
                                <div className="table-responsive">
                                    <table className="table table-hover">
                                        <thead style={{ backgroundColor: '#f8f9fa' }}>
                                            <tr>
                                                <th>Year</th>
                                                <th>Title</th>
                                                <th>Description</th>
                                                <th>PDF</th>
                                                <th>Published</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {reports.length === 0 ? (
                                                <tr>
                                                    <td colSpan="6" className="text-center text-muted py-4">
                                                        No annual reports found
                                                    </td>
                                                </tr>
                                            ) : (
                                                reports.map((report) => (
                                                    <tr key={report.id}>
                                                        <td><strong>{report.year}</strong></td>
                                                        <td>{report.title}</td>
                                                        <td><small>{report.description?.substring(0, 50)}...</small></td>
                                                        <td>
                                                            {report.pdf_url ? (
                                                                <a href={report.pdf_url} target="_blank" rel="noopener noreferrer" className="text-primary">
                                                                    View PDF
                                                                </a>
                                                            ) : (
                                                                <span className="text-muted">No PDF</span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <span className={`badge ${report.is_published ? 'bg-success' : 'bg-warning'}`}>
                                                                {report.is_published ? 'Published' : 'Draft'}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <button
                                                                className="btn btn-sm btn-outline-primary me-2"
                                                                onClick={() => handleEditReport(report)}
                                                                title="Edit"
                                                            >
                                                                <Edit2 size={16} />
                                                            </button>
                                                            <button
                                                                className="btn btn-sm btn-outline-danger"
                                                                onClick={() => handleDeleteReport(report.id)}
                                                                title="Delete"
                                                            >
                                                                <Trash2 size={16} />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Success Story Modal */}
            {showStoryModal && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header" style={{ backgroundColor: ARCA_GREEN, color: 'white' }}>
                                <h5 className="modal-title">
                                    {editingStory ? 'Edit Success Story' : 'Add Success Story'}
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={() => {
                                        setShowStoryModal(false);
                                        resetStoryForm();
                                    }}
                                ></button>
                            </div>
                            <form onSubmit={handleSaveStory}>
                                <div className="modal-body">
                                    <div className="mb-3">
                                        <label className="form-label">Title *</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={storyForm.title}
                                            onChange={(e) => setStoryForm({ ...storyForm, title: e.target.value })}
                                            required
                                        />
                                    </div>

                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Location</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={storyForm.location}
                                                onChange={(e) => setStoryForm({ ...storyForm, location: e.target.value })}
                                                placeholder="e.g., Nairobi, Kenya"
                                            />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Date</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={storyForm.date}
                                                onChange={(e) => setStoryForm({ ...storyForm, date: e.target.value })}
                                                placeholder="e.g., January 2025"
                                            />
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label">Description</label>
                                        <textarea
                                            className="form-control"
                                            rows="3"
                                            value={storyForm.description}
                                            onChange={(e) => setStoryForm({ ...storyForm, description: e.target.value })}
                                        />
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label">Impact</label>
                                        <textarea
                                            className="form-control"
                                            rows="2"
                                            value={storyForm.impact}
                                            onChange={(e) => setStoryForm({ ...storyForm, impact: e.target.value })}
                                        />
                                    </div>

                                    <div className="row">
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Category</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={storyForm.category}
                                                onChange={(e) => setStoryForm({ ...storyForm, category: e.target.value })}
                                                placeholder="e.g., Education"
                                            />
                                        </div>
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Icon</label>
                                            <select
                                                className="form-select"
                                                value={storyForm.icon}
                                                onChange={(e) => setStoryForm({ ...storyForm, icon: e.target.value })}
                                            >
                                                {iconOptions.map((opt) => (
                                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Display Order</label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                value={storyForm.display_order}
                                                onChange={(e) => setStoryForm({ ...storyForm, display_order: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => {
                                            setShowStoryModal(false);
                                            resetStoryForm();
                                        }}
                                    >
                                        <X size={18} className="me-2" />
                                        Cancel
                                    </button>
                                    <button type="submit" className="btn text-white" style={{ backgroundColor: ARCA_GREEN }}>
                                        <Save size={18} className="me-2" />
                                        Save Story
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Testimonial Modal */}
            {showTestimonialModal && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header" style={{ backgroundColor: ARCA_GREEN, color: 'white' }}>
                                <h5 className="modal-title">
                                    {editingTestimonial ? 'Edit Testimonial' : 'Add Testimonial'}
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={() => {
                                        setShowTestimonialModal(false);
                                        resetTestimonialForm();
                                    }}
                                ></button>
                            </div>
                            <form onSubmit={handleSaveTestimonial}>
                                <div className="modal-body">
                                    <div className="mb-3">
                                        <label className="form-label">Content *</label>
                                        <textarea
                                            className="form-control"
                                            rows="4"
                                            value={testimonialForm.content}
                                            onChange={(e) => setTestimonialForm({ ...testimonialForm, content: e.target.value })}
                                            required
                                        />
                                    </div>

                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Author *</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={testimonialForm.author}
                                                onChange={(e) => setTestimonialForm({ ...testimonialForm, author: e.target.value })}
                                                required
                                            />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Role</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={testimonialForm.role}
                                                onChange={(e) => setTestimonialForm({ ...testimonialForm, role: e.target.value })}
                                                placeholder="e.g., Country Director"
                                            />
                                        </div>
                                    </div>

                                    <div className="row">
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Country</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={testimonialForm.country}
                                                onChange={(e) => setTestimonialForm({ ...testimonialForm, country: e.target.value })}
                                            />
                                        </div>
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Rating</label>
                                            <select
                                                className="form-select"
                                                value={testimonialForm.rating}
                                                onChange={(e) => setTestimonialForm({ ...testimonialForm, rating: parseInt(e.target.value) })}
                                            >
                                                <option value="5">5 Stars</option>
                                                <option value="4">4 Stars</option>
                                                <option value="3">3 Stars</option>
                                                <option value="2">2 Stars</option>
                                                <option value="1">1 Star</option>
                                            </select>
                                        </div>
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">Display Order</label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                value={testimonialForm.display_order}
                                                onChange={(e) => setTestimonialForm({ ...testimonialForm, display_order: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => {
                                            setShowTestimonialModal(false);
                                            resetTestimonialForm();
                                        }}
                                    >
                                        <X size={18} className="me-2" />
                                        Cancel
                                    </button>
                                    <button type="submit" className="btn text-white" style={{ backgroundColor: ARCA_GREEN }}>
                                        <Save size={18} className="me-2" />
                                        Save Testimonial
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Annual Report Modal */}
            {showReportModal && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header" style={{ backgroundColor: ARCA_GREEN, color: 'white' }}>
                                <h5 className="modal-title">
                                    {editingReport ? 'Edit Annual Report' : 'Add Annual Report'}
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={() => {
                                        setShowReportModal(false);
                                        resetReportForm();
                                    }}
                                ></button>
                            </div>
                            <form onSubmit={handleSaveReport}>
                                <div className="modal-body">
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Year *</label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                value={reportForm.year}
                                                onChange={(e) => setReportForm({ ...reportForm, year: e.target.value })}
                                                required
                                            />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Title *</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={reportForm.title}
                                                onChange={(e) => setReportForm({ ...reportForm, title: e.target.value })}
                                                required
                                                placeholder="e.g., ARCA Annual Report 2025"
                                            />
                                        </div>
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label">Description</label>
                                        <textarea
                                            className="form-control"
                                            rows="3"
                                            value={reportForm.description}
                                            onChange={(e) => setReportForm({ ...reportForm, description: e.target.value })}
                                        />
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label">Cover Image URL</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={reportForm.cover_image_url}
                                            onChange={(e) => setReportForm({ ...reportForm, cover_image_url: e.target.value })}
                                            placeholder="https://..."
                                        />
                                    </div>

                                    <div className="row">
                                        <div className="col-md-8 mb-3">
                                            <label className="form-label">PDF URL</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={reportForm.pdf_url}
                                                onChange={(e) => setReportForm({ ...reportForm, pdf_url: e.target.value })}
                                                placeholder="Upload PDF first or enter URL"
                                            />
                                        </div>
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label">File Size</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={reportForm.file_size}
                                                onChange={(e) => setReportForm({ ...reportForm, file_size: e.target.value })}
                                                placeholder="e.g., 2.5 MB"
                                            />
                                        </div>
                                    </div>

                                    <div className="form-check mb-3">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            checked={reportForm.is_published}
                                            onChange={(e) => setReportForm({ ...reportForm, is_published: e.target.checked })}
                                            id="publishedCheck"
                                        />
                                        <label className="form-check-label" htmlFor="publishedCheck">
                                            Publish (make visible to public)
                                        </label>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => {
                                            setShowReportModal(false);
                                            resetReportForm();
                                        }}
                                    >
                                        <X size={18} className="me-2" />
                                        Cancel
                                    </button>
                                    <button type="submit" className="btn text-white" style={{ backgroundColor: ARCA_GREEN }}>
                                        <Save size={18} className="me-2" />
                                        Save Report
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ImpactManagement;
